<template>
    <div class="gcb">
        <!-- A régua inteira é o gatilho do popover (mesmo padrão da barrinha de andamento dos pedidos) -->
        <InfoPopoverCmp class="gcb-pop" title="Variações no canal" :label="`${summary}. Ver detalhes`" :width="300">
            <template #trigger>
                <span class="gcb-bar" :class="{'is-stacked': stacked}" aria-hidden="true">
                    <template v-if="stacked">
                        <i v-for="slice in slices" :key="slice.status" class="gcb-seg" :class="`is-${slice.status}`"
                            :style="{flexGrow: slice.count}"></i>
                    </template>
                    <template v-else>
                        <i v-for="(item, index) in items" :key="item.idMuven" class="gcb-seg" :class="`is-${item.status}`"
                            :style="{animationDelay: `${Math.min(index, 24) * 30}ms`}"></i>
                    </template>
                </span>
            </template>

            <ul class="gcb-list">
                <li v-for="item in sortedItems" :key="item.idMuven" :class="`is-${item.status}`">
                    <span class="gcb-dot" aria-hidden="true"></span>
                    <span class="gcb-name" :title="item.label">{{item.label || 'Variação'}}</span>
                    <span class="gcb-state">{{statusText[item.status]}}</span>
                    <code v-if="item.currentId" class="gcb-id">{{item.currentId}}</code>
                </li>
            </ul>
        </InfoPopoverCmp>

        <p class="gcb-legend">
            <span><strong>{{counts.ok}}</strong> de {{items.length}} {{items.length === 1 ? 'variação confere' : 'variações conferem'}}</span>
            <span v-if="counts.checking" class="gcb-chip">conferindo {{counts.checking}}</span>
            <span v-if="counts.bad" class="gcb-chip is-bad">{{counts.bad}} não {{counts.bad === 1 ? 'encontrada' : 'encontradas'}}</span>
            <span v-if="counts.empty" class="gcb-chip is-empty">{{counts.empty}} sem associação</span>
            <span v-if="counts.unknown" class="gcb-chip">{{counts.unknown}} sem conferência</span>
        </p>
    </div>
</template>

<script>
import InfoPopoverCmp from '@/utils/components/InfoPopoverCmp.vue'

const STATUS_TEXT = {
    ok: 'Confere',
    bad: 'Não encontrada',
    empty: 'Sem associação',
    checking: 'Conferindo',
    unknown: 'Sem conferência',
}

// Acima disso os segmentos ficam finos demais: vira uma barra só, proporcional
const MAX_SEGMENTS = 24

// Barra: o que está bem primeiro, os problemas no fim. Lista: problemas primeiro.
const BAR_ORDER = ['ok', 'unknown', 'checking', 'empty', 'bad']
const LIST_ORDER = ['bad', 'checking', 'unknown', 'empty', 'ok']

/**
 * Régua da grade no card do canal. Cabe em qualquer largura: os segmentos
 * dividem o espaço (flex 1 1 0, sem largura mínima) e o container corta o resto.
 * items: [{idMuven, label, currentId, status: ok | bad | empty | checking | unknown}]
 */
export default {
    name: 'GridCoverageBarCmp',
    components: {
        InfoPopoverCmp,
    },
    props: {
        items: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            statusText: STATUS_TEXT,
        }
    },
    computed: {
        counts() {
            return this.items.reduce((acc, item) => {
                acc[item.status] = (acc[item.status] || 0) + 1
                return acc
            }, {ok: 0, bad: 0, empty: 0, checking: 0, unknown: 0})
        },
        summary() {
            return `${this.counts.ok} de ${this.items.length} variações conferem no canal`
        },
        stacked() {
            return this.items.length > MAX_SEGMENTS
        },
        slices() {
            return BAR_ORDER
                .filter((status) => this.counts[status] > 0)
                .map((status) => ({status, count: this.counts[status]}))
        },
        sortedItems() {
            return [...this.items].sort((a, b) => LIST_ORDER.indexOf(a.status) - LIST_ORDER.indexOf(b.status))
        },
    },
}
</script>

<style lang="scss" scoped>
$mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;

.gcb {
    display: grid;
    gap: 6px;
    min-width: 0;
    margin-top: 10px;
}

// O gatilho do popover ocupa a largura do card (por padrão ele é inline)
.gcb-pop {
    display: flex;
    min-width: 0;

    ::v-deep .ipo-trigger.is-custom {
        display: flex;
        width: 100%;
        min-width: 0;
        padding: 4px 0;
        cursor: pointer;
    }
}

.gcb-bar {
    display: flex;
    gap: 2px;
    width: 100%;
    min-width: 0;
    height: 8px;
    overflow: hidden;
    border-radius: 5px;

    // Uma barra só: as fatias encostam, só com um fio entre elas
    &.is-stacked {
        gap: 1px;
    }
}

.gcb-seg {
    flex: 1 1 0;
    min-width: 0;
    background: var(--border-subtle);
    animation: gcb-in .45s cubic-bezier(.22, 1, .36, 1) both;
    transform-origin: left center;
    transition: background-color .3s, flex-grow .3s;

    &.is-ok {
        background: var(--success-solid);
    }

    &.is-bad {
        background: var(--danger-solid);
    }

    // Sem associação: vazio de verdade, só a hachura
    &.is-empty {
        background: repeating-linear-gradient(135deg, var(--border-strong) 0 1.5px, transparent 1.5px 4px);
    }

    &.is-checking {
        background: var(--border);
        animation: gcb-in .45s cubic-bezier(.22, 1, .36, 1) both, gcb-pulse 1.1s ease-in-out infinite alternate;
    }

    &.is-unknown {
        background: var(--border);
    }

    // Na barra proporcional, 1 problema em 500 não pode sumir
    .is-stacked > &.is-bad,
    .is-stacked > &.is-empty,
    .is-stacked > &.is-checking {
        min-width: 3px;
    }
}

.gcb-legend {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 8px;
    min-width: 0;
    margin: 0;
    color: var(--text-muted);
    font-size: 12px;

    strong {
        color: var(--text-strong);
        font-size: 13px;
    }
}

.gcb-chip {
    padding: 1px 7px;
    border-radius: 999px;
    color: var(--text-muted);
    background: var(--surface-2);
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;

    &.is-bad {
        color: var(--danger-fg);
        background: var(--danger-bg);
    }

    &.is-empty {
        box-shadow: inset 0 0 0 1px var(--border-strong);
        background: transparent;
    }
}

// Conteúdo do popover (vai para o <body>, com o fundo escuro dos tooltips)
.gcb-list {
    max-height: 260px;
    margin: 8px -4px 0 0;
    padding: 0 4px 0 0;
    overflow-y: auto;
    list-style: none;

    li {
        display: grid;
        grid-template-columns: 10px minmax(0, 1fr) auto;
        column-gap: 8px;
        align-items: center;
        padding: 4px 0;
        color: var(--text-on-tooltip-soft);

        & + li {
            border-top: 1px solid color-mix(in srgb, var(--text-on-tooltip) 10%, transparent);
        }
    }
}

.gcb-dot {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    background: color-mix(in srgb, var(--text-on-tooltip) 30%, transparent);

    .is-ok > & {
        background: var(--success-solid);
    }

    .is-bad > & {
        background: var(--danger-solid);
    }

    .is-empty > & {
        background: transparent;
        box-shadow: inset 0 0 0 1.5px color-mix(in srgb, var(--text-on-tooltip) 40%, transparent);
    }

    .is-checking > & {
        animation: gcb-pulse 1.1s ease-in-out infinite alternate;
    }
}

.gcb-name {
    overflow: hidden;
    color: var(--text-on-tooltip);
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.gcb-state {
    color: var(--text-on-tooltip-muted);
    font-size: 11px;
    white-space: nowrap;

    .is-bad > & {
        color: var(--danger-on-dark);
        font-weight: 700;
    }
}

.gcb-id {
    grid-column: 2 / -1;
    color: var(--text-on-tooltip-muted);
    font-family: $mono;
    font-size: 11px;
    overflow-wrap: anywhere;
}

@keyframes gcb-in {
    from {
        opacity: 0;
        transform: scaleX(.2);
    }
}

@keyframes gcb-pulse {
    to {
        opacity: .45;
    }
}

@media (prefers-reduced-motion: reduce) {
    .gcb-seg,
    .gcb-seg.is-checking,
    .gcb-dot {
        animation: none;
    }
}
</style>
